import { API_ROUTES } from "./ApiRoutes";
import { apiClient } from "./http.api";

export const transcriptApi = {
  // Get sessions list with filters
  getSessions: async (params = {}) => {
    const response = await apiClient.get(API_ROUTES.SESSIONS.LIST, { params });
    return response.data;
  },

  // Get single session
  getSessionById: async (id) => {
    const response = await apiClient.get(API_ROUTES.SESSIONS.GET_BY_ID(id));
    return response.data;
  },

  // Get sessions for given event
  getSessionsByEvent: async (eventId, params = {}) => {
    const response = await apiClient.get(API_ROUTES.SESSIONS.LIST, { params: { ...params, event_id: eventId } });
    return response.data;
  },

  // Update session (save report markdown fields)
  updateSession: async (id, payload) => {
    const response = await apiClient.put(API_ROUTES.SESSIONS.UPDATE(id), payload);
    return response.data;
  },

  // Delete session
  deleteSession: async (id) => {
    const response = await apiClient.delete(API_ROUTES.SESSIONS.DELETE(id));
    return response;
  },

  // Start transcript generation for a session
  startTranscriptGeneration: async (sessionId, provider = 'whisper-localhost') => {
    const response = await apiClient.post(API_ROUTES.SESSIONS.START_TRANSCRIPT(sessionId), {
      provider
    });
    return response.data;
  },

  // Regenerate transcript (delete old transcript and prepare for new generation)
  regenerateTranscript: async (sessionId) => {
    const response = await apiClient.post(API_ROUTES.SESSIONS.REGENERATE_TRANSCRIPT(sessionId));
    return response.data;
  },

  // Get queue status for a session's transcript processing
  getSessionQueueStatus: async (sessionId) => {
    const response = await apiClient.get(API_ROUTES.SESSIONS.QUEUE_STATUS(sessionId));
    return response.data;
  },

  // Get lightweight chunk progress (for efficient polling)
  getChunkProgress: async (sessionId) => {
    const response = await apiClient.get(API_ROUTES.SESSIONS.CHUNK_PROGRESS(sessionId));
    return response.data;
  },

  // Download media file for a session
  downloadSessionMedia: async (sessionId) => {
    // Get session info first to get filename
    const session = await apiClient.get(`/api/reports/sessions/${sessionId}`);
    const filename = session.data.original_filename || session.data.originalFilename || `session_${sessionId}_media`;
    
    // Use the session video endpoint which reads from storage_path
    const response = await apiClient.get(`/api/reports/sessions/${sessionId}/video`, {
      responseType: 'blob',
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return response.data;
  },

  // Get transcript for a session
  getTranscript: async (sessionId) => {
    const response = await apiClient.get(`/api/reports/transcripts/${sessionId}`);
    return response.data;
  },

  // Get transcript segments in pages (for very long transcripts)
  getTranscriptSegments: async (sessionId, params = {}) => {
    const response = await apiClient.get(`/api/reports/transcripts/${sessionId}/segments`, { params });
    return response.data;
  },

  // Update transcript for a session (for editor auto-save)
  updateTranscript: async (sessionId, transcriptData) => {
    const response = await apiClient.put(`/api/reports/transcripts/${sessionId}`, transcriptData);
    return response.data;
  },

  // Export transcript in various formats
  exportTranscript: async (sessionId, format = 'srt') => {
    const response = await apiClient.get(`/api/reports/transcripts/${sessionId}/export`, {
      params: { format },
      responseType: 'blob',
    });
    return response.data;
  },

  // Manual save (creates new version)
  saveTranscriptVersion: async (sessionId, transcriptData, description = null) => {
    const response = await apiClient.put(
      `/api/reports/transcripts/${sessionId}`,
      transcriptData,
      { params: { save_as_version: true, version_description: description } }
    );
    return response.data;
  },

  // Get version history
  getVersionHistory: async (sessionId) => {
    const response = await apiClient.get(`/api/reports/transcripts/${sessionId}/versions`);
    return response.data;
  },

  // Restore specific version
  restoreVersion: async (sessionId, versionId) => {
    const response = await apiClient.get(`/api/reports/transcripts/${sessionId}/versions/${versionId}`);
    return response.data;
  },

  // Rename speaker across all blocks
  renameSpeaker: async (sessionId, oldName, newName) => {
    const response = await apiClient.put(
      `/api/reports/transcripts/${sessionId}/speakers/rename`,
      null,
      { params: { old_name: oldName, new_name: newName } }
    );
    return response.data;
  },

  // Delete speaker and merge to another
  deleteSpeaker: async (sessionId, speakerName, mergeToSpeaker) => {
    const response = await apiClient.delete(
      `/api/reports/transcripts/${sessionId}/speakers/${speakerName}`,
      { params: { merge_to_speaker: mergeToSpeaker } }
    );
    return response.data;
  },

  // Lock management for transcript editing
  acquireLock: async (sessionId, force = false) => {
    const response = await apiClient.post(
      `/api/reports/transcripts/${sessionId}/lock`,
      null,
      { params: { force } }
    );
    return response.data;
  },

  releaseLock: async (sessionId) => {
    const response = await apiClient.delete(
      `/api/reports/transcripts/${sessionId}/lock`
    );
    return response.data;
  },

  getLockStatus: async (sessionId) => {
    const response = await apiClient.get(
      `/api/reports/transcripts/${sessionId}/lock-status`
    );
    return response.data;
  },

  forceOverrideLock: async (sessionId) => {
    const response = await apiClient.post(
      `/api/reports/transcripts/${sessionId}/lock`,
      null,
      { params: { force: true } }
    );
    return response.data;
  },

  // Get all transcripts
  getAll: async () => {
    const response = await apiClient.get(API_ROUTES.TRANSCRIPTS.LIST);
    return response.data;
  },

  // Get transcript by ID
  getById: async (id) => {
    const response = await apiClient.get(API_ROUTES.TRANSCRIPTS.GET_BY_ID(id));
    return response.data;
  },

  // Create new transcript
  create: async (transcriptData) => {
    const response = await apiClient.post(API_ROUTES.TRANSCRIPTS.CREATE, transcriptData);
    return response.data;
  },

  // Update transcript
  update: async (id, transcriptData) => {
    const response = await apiClient.put(API_ROUTES.TRANSCRIPTS.UPDATE(id), transcriptData);
    return response.data;
  },

  // Delete transcript
  delete: async (id) => {
    const response = await apiClient.delete(API_ROUTES.TRANSCRIPTS.DELETE(id));
    return response.data;
  },

  // Upload transcript file
  upload: async (fileData) => {
    const response = await apiClient.post(API_ROUTES.TRANSCRIPTS.UPLOAD, fileData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Create session
  createSession: async (sessionData) => {
    const response = await apiClient.post(API_ROUTES.SESSIONS.CREATE, sessionData);
    return response.data;
  },
};

// Export individual functions for backward compatibility
export const {
  getAll,
  getById,
  create,
  update,
  delete: deleteTranscript,
  upload,
  getSessions,
  getSessionById,
  getSessionsByEvent,
  updateSession,
  createSession,
  deleteSession,
  startTranscriptGeneration,
  getSessionQueueStatus,
  getChunkProgress,
  getTranscript,
  updateTranscript,
  exportTranscript,
  saveTranscriptVersion,
  getVersionHistory,
  restoreVersion,
  renameSpeaker,
  deleteSpeaker,
  acquireLock,
  releaseLock,
  getLockStatus,
  forceOverrideLock,
  downloadSessionMedia,
} = transcriptApi;

// Default export
export default transcriptApi;
